import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ECMR, DigitalSignature } from '../types/eCMR';
import { loadECMRFromLocalStorage, saveECMR } from '../services/eCMRService';
import { validateSignatureStatus } from '../services/signatureService';
import SignatureCapture from './SignatureCapture';

const SignECMR: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ecmr, setEcmr] = useState<ECMR | null>(null);
  const [currentSigner, setCurrentSigner] = useState<'Consignor' | 'Carrier' | 'Consignee'>('Consignor');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signatureStatus, setSignatureStatus] = useState<{
    complete: boolean;
    validSignatures: number;
    totalRequired: number;
    missingSignatures: string[];
  }>({
    complete: false,
    validSignatures: 0,
    totalRequired: 3,
    missingSignatures: ['Remitente', 'Transportista', 'Destinatario']
  });

  useEffect(() => {
    if (!id) {
      setError('ID de e-CMR no proporcionado');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        const loadedEcmr = loadECMRFromLocalStorage(id);
        if (!loadedEcmr) {
          setError('e-CMR no encontrado');
          setLoading(false);
          return;
        }

        setEcmr(loadedEcmr);
        
        // Verificar estado de firmas
        const status = validateSignatureStatus(
          loadedEcmr.ExchangedDocument?.ConsignorDigitalSignature,
          loadedEcmr.ExchangedDocument?.CarrierDigitalSignature,
          loadedEcmr.ExchangedDocument?.ConsigneeDigitalSignature
        );
        setSignatureStatus(status);
        
        setLoading(false);
      } catch (err) {
        setError(`Error al cargar e-CMR: ${(err as Error).message}`);
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleSaveSignature = (signatureImage: string) => {
    if (!ecmr || !ecmr.ExchangedDocument) return;
    
    const now = new Date().toISOString();
    
    const signature: DigitalSignature = {
      SignatoryPartyRole: currentSigner,
      SigningTime: now,
      SignatureValue: signatureImage,
      SignatureMethod: 'manual-capture'
    };
    
    saveSignatureToECMR(signature);
  };
  
  const saveSignatureToECMR = (signature: DigitalSignature) => {
    if (!ecmr || !id) return;
    
    const updatedEcmr = { ...ecmr };
    if (!updatedEcmr.ExchangedDocument) {
      updatedEcmr.ExchangedDocument = {};
    }
    
    if (signature.SignatoryPartyRole === 'Consignor') {
      updatedEcmr.ExchangedDocument.ConsignorDigitalSignature = signature;
    } else if (signature.SignatoryPartyRole === 'Carrier') {
      updatedEcmr.ExchangedDocument.CarrierDigitalSignature = signature;
    } else if (signature.SignatoryPartyRole === 'Consignee') {
      updatedEcmr.ExchangedDocument.ConsigneeDigitalSignature = signature;
    }
    
    // Actualizar estado local
    setEcmr(updatedEcmr);
    
    // Guardar en localStorage
    saveECMR(updatedEcmr);
    
    // Actualizar estado de firmas
    const status = validateSignatureStatus(
      updatedEcmr.ExchangedDocument?.ConsignorDigitalSignature,
      updatedEcmr.ExchangedDocument?.CarrierDigitalSignature,
      updatedEcmr.ExchangedDocument?.ConsigneeDigitalSignature
    );
    setSignatureStatus(status);
    
    alert(`Firma de ${getSignerName(signature.SignatoryPartyRole)} guardada correctamente`);
  };
  
  const getSignerName = (signer: string): string => {
    switch(signer) {
      case 'Consignor': return 'Remitente';
      case 'Carrier': return 'Transportista';
      case 'Consignee': return 'Destinatario';
      default: return signer;
    }
  };

  const getSignatureStatus = (signer: 'Consignor' | 'Carrier' | 'Consignee'): { signed: boolean, date?: string, method?: string } => {
    if (!ecmr?.ExchangedDocument) return { signed: false };
    
    let signature;
    if (signer === 'Consignor') {
      signature = ecmr.ExchangedDocument.ConsignorDigitalSignature;
    } else if (signer === 'Carrier') {
      signature = ecmr.ExchangedDocument.CarrierDigitalSignature;
    } else {
      signature = ecmr.ExchangedDocument.ConsigneeDigitalSignature;
    }
    
    if (!signature) return { signed: false };
    
    // Formatear fecha
    let date = signature.SigningTime;
    try {
      const dateObj = new Date(signature.SigningTime);
      date = dateObj.toLocaleString();
    } catch (e) {
      // Mantener formato original si hay error
    }
    
    return { 
      signed: true,
      date,
      method: signature.SignatureMethod
    };
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-center">
          <p className="text-lg">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error || !ecmr) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error || 'Error desconocido'}</p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Volver a la lista
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Firmar documento e-CMR</h1>
      
      <div className="bg-white shadow-md rounded p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 border-b pb-2">Información del documento</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="font-medium">ID:</p>
            <p>{ecmr.ExchangedDocument?.ID || 'Sin ID'}</p>
          </div>
          <div>
            <p className="font-medium">Fecha de emisión:</p>
            <p>{ecmr.ExchangedDocument?.IssueDateTime?.DateTimeString || 'Sin fecha'}</p>
          </div>
          <div>
            <p className="font-medium">Remitente:</p>
            <p>{ecmr.SpecifiedSupplyChainConsignment?.ConsignorTradeParty?.Name?.[0] || 'Sin remitente'}</p>
          </div>
          <div>
            <p className="font-medium">Transportista:</p>
            <p>{ecmr.SpecifiedSupplyChainConsignment?.CarrierTradeParty?.Name?.[0] || 'Sin transportista'}</p>
          </div>
          <div>
            <p className="font-medium">Destinatario:</p>
            <p>{ecmr.SpecifiedSupplyChainConsignment?.ConsigneeTradeParty?.Name?.[0] || 'Sin destinatario'}</p>
          </div>
        </div>
      </div>
      
      <div className="bg-white shadow-md rounded p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 border-b pb-2">Estado de firmas</h2>
        
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <div className={`w-3 h-3 rounded-full mr-2 ${signatureStatus.complete ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
            <p>Estado general: {signatureStatus.complete ? 'Completo' : 'Pendiente'}</p>
          </div>
          <p>Firmas: {signatureStatus.validSignatures} de {signatureStatus.totalRequired}</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`p-4 rounded ${getSignatureStatus('Consignor').signed ? 'bg-green-100 border-green-500' : 'bg-gray-100 border-gray-300'} border`}>
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Remitente</h3>
              {getSignatureStatus('Consignor').signed ? (
                <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">Firmado</span>
              ) : (
                <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full">Pendiente</span>
              )}
            </div>
            {getSignatureStatus('Consignor').signed && (
              <p className="text-sm mt-2">Firmado el {getSignatureStatus('Consignor').date}</p>
            )}
          </div>
          
          <div className={`p-4 rounded ${getSignatureStatus('Carrier').signed ? 'bg-green-100 border-green-500' : 'bg-gray-100 border-gray-300'} border`}>
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Transportista</h3>
              {getSignatureStatus('Carrier').signed ? (
                <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">Firmado</span>
              ) : (
                <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full">Pendiente</span>
              )}
            </div>
            {getSignatureStatus('Carrier').signed && (
              <p className="text-sm mt-2">Firmado el {getSignatureStatus('Carrier').date}</p>
            )}
          </div>
          
          <div className={`p-4 rounded ${getSignatureStatus('Consignee').signed ? 'bg-green-100 border-green-500' : 'bg-gray-100 border-gray-300'} border`}>
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Destinatario</h3>
              {getSignatureStatus('Consignee').signed ? (
                <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">Firmado</span>
              ) : (
                <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full">Pendiente</span>
              )}
            </div>
            {getSignatureStatus('Consignee').signed && (
              <p className="text-sm mt-2">Firmado el {getSignatureStatus('Consignee').date}</p>
            )}
          </div>
        </div>
      </div>
      
      <div className="bg-white shadow-md rounded p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 border-b pb-2">Firma del documento</h2>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Seleccione quién firmará:
          </label>
          <select
            value={currentSigner}
            onChange={(e) => setCurrentSigner(e.target.value as 'Consignor' | 'Carrier' | 'Consignee')}
            className="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full md:w-1/3"
          >
            <option value="Consignor">Remitente</option>
            <option value="Carrier">Transportista</option>
            <option value="Consignee">Destinatario</option>
          </select>
        </div>
        
        <SignatureCapture 
          onSave={handleSaveSignature}
          role={getSignerName(currentSigner)}
        />
      </div>
      
      <div className="flex space-x-4">
        <Link 
          to={`/view/${id}`}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          <i className="fas fa-eye mr-1"></i> Ver documento
        </Link>
        <button
          onClick={() => navigate('/')}
          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
        >
          <i className="fas fa-arrow-left mr-1"></i> Volver a la lista
        </button>
      </div>
    </div>
  );
};

export default SignECMR; 